import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { useImportantNotes } from '../contexts/ImportantNotesContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline button + dialog to manage important notes attached to a prospect.
 *
 * Props:
 *   prospectId : string (required)
 *   prospectName : string (shown in dialog header)
 *   variant : 'header' | 'icon-only' | 'badge' (controls how the trigger renders)
 *   onChange(count) : callback fired whenever the note count changes
 */
export default function ImportantNotesButton({
    prospectId,
    prospectName = '',
    variant = 'header',
    onChange,
}) {
    const { user } = useAuth();
    const { flags, setProspectCount } = useImportantNotes();
    const localCount = flags[prospectId] || 0;

    const [open, setOpen] = useState(false);
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newContent, setNewContent] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editingContent, setEditingContent] = useState('');
    const [saving, setSaving] = useState(false);

    const role = user?.role;
    const isPrivileged = role === 'admin' || role === 'responsable';

    const canModifyNote = useCallback(
        (note) => isPrivileged || note.created_by === user?.id,
        [isPrivileged, user?.id]
    );

    const fetchNotes = useCallback(async () => {
        if (!prospectId) return;
        setLoading(true);
        try {
            const res = await axios.get(
                `${API_URL}/api/prospects/${prospectId}/notes-importantes`,
                { withCredentials: true }
            );
            const list = res.data || [];
            setNotes(list);
            setProspectCount(prospectId, list.length);
            if (onChange) onChange(list.length);
        } catch {
            toast.error('Impossible de charger les notes importantes');
        } finally {
            setLoading(false);
        }
    }, [prospectId, setProspectCount, onChange]);

    useEffect(() => {
        if (open) fetchNotes();
    }, [open, fetchNotes]);

    const handleCreate = async () => {
        const content = newContent.trim();
        if (!content) return;
        setSaving(true);
        try {
            const res = await axios.post(
                `${API_URL}/api/prospects/${prospectId}/notes-importantes`,
                { content },
                { withCredentials: true }
            );
            const next = [res.data, ...notes];
            setNotes(next);
            setProspectCount(prospectId, next.length);
            if (onChange) onChange(next.length);
            setNewContent('');
            toast.success('Note ajoutée');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur ajout note');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (note) => {
        setEditingId(note.id);
        setEditingContent(note.content);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingContent('');
    };

    const handleUpdate = async (noteId) => {
        const content = editingContent.trim();
        if (!content) return;
        setSaving(true);
        try {
            const res = await axios.put(
                `${API_URL}/api/prospects/${prospectId}/notes-importantes/${noteId}`,
                { content },
                { withCredentials: true }
            );
            setNotes(notes.map(n => (n.id === noteId ? res.data : n)));
            cancelEdit();
            toast.success('Note modifiée');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur modification');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('Supprimer cette note importante ?')) return;
        try {
            await axios.delete(
                `${API_URL}/api/prospects/${prospectId}/notes-importantes/${noteId}`,
                { withCredentials: true }
            );
            const next = notes.filter(n => n.id !== noteId);
            setNotes(next);
            setProspectCount(prospectId, next.length);
            if (onChange) onChange(next.length);
            toast.success('Note supprimée');
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Erreur suppression');
        }
    };

    const hasNotes = localCount > 0;

    // -- Trigger variants ----------------------------------------------------
    const trigger = (() => {
        if (variant === 'icon-only') {
            if (!hasNotes) return null;
            return (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    title={`${localCount} note(s) importante(s)`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-red-100 text-red-700 hover:bg-red-200 ring-2 ring-red-300 transition"
                    data-testid="important-notes-icon-btn"
                >
                    <AlertTriangle className="w-4 h-4" />
                </button>
            );
        }
        if (variant === 'badge') {
            if (!hasNotes) return null;
            // Compact pill button (sits in tight rows / list cells)
            return (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                    title={`${localCount} note(s) importante(s)`}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 ring-1 ring-red-300 transition"
                    data-testid="important-notes-badge-btn"
                >
                    <AlertTriangle className="w-3 h-3" />
                    {localCount}
                </button>
            );
        }
        // default 'header'
        return (
            <Button
                variant="outline"
                onClick={() => setOpen(true)}
                className={`gap-2 border-2 transition ${
                    hasNotes
                        ? 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
                data-testid="important-notes-btn"
            >
                <AlertTriangle className="w-4 h-4" />
                Important
                {hasNotes && (
                    <span
                        className="ml-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full bg-red-600 text-white"
                        data-testid="important-notes-count"
                    >
                        {localCount}
                    </span>
                )}
            </Button>
        );
    })();

    return (
        <>
            {trigger}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl" data-testid="important-notes-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="w-5 h-5" />
                            Notes importantes
                            {prospectName && <span className="text-gray-700 font-normal">— {prospectName}</span>}
                        </DialogTitle>
                        <DialogDescription>
                            Ces notes suivent le prospect tout au long de son parcours (devis, facture, litige…).
                        </DialogDescription>
                    </DialogHeader>

                    {/* New note form */}
                    <div className="space-y-2 border-b pb-4">
                        <Textarea
                            value={newContent}
                            onChange={(e) => setNewContent(e.target.value)}
                            placeholder="Saisir une note importante (max 4000 caractères)…"
                            rows={3}
                            className="resize-none"
                            maxLength={4000}
                            data-testid="important-notes-new-textarea"
                        />
                        <div className="flex justify-end">
                            <Button
                                onClick={handleCreate}
                                disabled={!newContent.trim() || saving}
                                className="gap-2 bg-red-600 hover:bg-red-700 text-white"
                                data-testid="important-notes-add-btn"
                            >
                                <Plus className="w-4 h-4" />
                                Ajouter
                            </Button>
                        </div>
                    </div>

                    {/* Notes list */}
                    <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
                        {loading ? (
                            <div className="text-center text-gray-400 py-6">Chargement…</div>
                        ) : notes.length === 0 ? (
                            <div className="text-center text-gray-400 py-6 italic">
                                Aucune note importante pour ce prospect.
                            </div>
                        ) : (
                            notes.map((note) => {
                                const editable = canModifyNote(note);
                                const isEditing = editingId === note.id;
                                return (
                                    <div
                                        key={note.id}
                                        className="bg-red-50 border-l-4 border-red-400 rounded p-3"
                                        data-testid={`important-note-${note.id}`}
                                    >
                                        {isEditing ? (
                                            <div className="space-y-2">
                                                <Textarea
                                                    value={editingContent}
                                                    onChange={(e) => setEditingContent(e.target.value)}
                                                    rows={3}
                                                    maxLength={4000}
                                                    className="resize-none"
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={cancelEdit}
                                                        className="gap-1"
                                                    >
                                                        <X className="w-3 h-3" /> Annuler
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleUpdate(note.id)}
                                                        disabled={!editingContent.trim() || saving}
                                                        className="gap-1 bg-red-600 hover:bg-red-700 text-white"
                                                    >
                                                        <Check className="w-3 h-3" /> Enregistrer
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="whitespace-pre-wrap text-sm text-gray-900 mb-2">
                                                    {note.content}
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-gray-500">
                                                    <div>
                                                        <span className="font-semibold text-red-700">
                                                            {note.created_by_name}
                                                        </span>
                                                        {' • '}
                                                        {note.created_at
                                                            ? format(new Date(note.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })
                                                            : ''}
                                                        {note.updated_at && (
                                                            <span className="italic text-gray-400">
                                                                {' (modifié le '}
                                                                {format(new Date(note.updated_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                                                {note.updated_by_name ? ` par ${note.updated_by_name}` : ''}
                                                                {')'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {editable && (
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={() => startEdit(note)}
                                                                className="text-gray-500 hover:text-blue-600 p-1"
                                                                title="Modifier"
                                                                data-testid={`important-note-edit-${note.id}`}
                                                            >
                                                                <Pencil className="w-3.5 h-3.5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(note.id)}
                                                                className="text-gray-500 hover:text-red-600 p-1"
                                                                title="Supprimer"
                                                                data-testid={`important-note-delete-${note.id}`}
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
